import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import SafeHtml from "../../components/SafeHtml";
import {
  ArrowLeft, CheckCircle, ChevronLeft, ChevronRight,
  Play, FileText, ExternalLink, ClipboardList, Award, Lock, AlertCircle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AcademyAula = () => {

  const { aulaId } = useParams();
  const navigate = useNavigate();
  const [aula, setAula] = useState(null);
  const [modulo, setModulo] = useState(null);
  const [todasAulas, setTodasAulas] = useState([]);
  const [concluida, setConcluida] = useState(false);
  const [loading, setLoading] = useState(true);
  const [questionarios, setQuestionarios] = useState([]);
  const [statusQuestionarios, setStatusQuestionarios] = useState(null);
  const [progressoModulo, setProgressoModulo] = useState({ total: 0, concluidas: 0 });
  
  const token = localStorage.getItem('tayane_token');

  useEffect(() => {
    fetchAula();
  }, [aulaId]);

  const fetchAula = async () => {
    try {
      const aulaRes = await axios.get(`${BACKEND_URL}/api/academy/aulas/${aulaId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAula(aulaRes.data);
      
      // Buscar todas as aulas do módulo para navegação
      const [aulasRes, questionariosRes, statusRes, progressoRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/academy/modulos/${aulaRes.data.modulo_id}/aulas`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/academy/modulos/${aulaRes.data.modulo_id}/questionarios`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/academy/modulos/${aulaRes.data.modulo_id}/status-questionarios`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${BACKEND_URL}/api/academy/meu-progresso`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setTodasAulas(Array.isArray(aulasRes.data) ? aulasRes.data : (aulasRes.data?.aulas || aulasRes.data?.items || aulasRes.data?.data || []));
      setQuestionarios(Array.isArray(questionariosRes.data) ? questionariosRes.data : (questionariosRes.data?.questionarios || questionariosRes.data?.items || questionariosRes.data?.data || []));
      setStatusQuestionarios(statusRes.data);
      // Verificar se esta aula já foi concluída
      const jaFeitas = (Array.isArray(progressoRes.data) ? progressoRes.data : (progressoRes.data?.items || [])).filter(p => p.concluida).map(p => p.aula_id);
      setConcluida(jaFeitas.includes(aulaId));
      
    } catch (error) {
      console.error("Erro ao carregar aula:", error);
      toast.error("Erro ao carregar aula");
      navigate("/crm/academy");
    } finally {
      setLoading(false);
    }
  };

  const marcarConcluida = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/academy/progresso`, {
        aula_id: aulaId,
        concluida: !concluida
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setConcluida(!concluida);
      toast.success(concluida ? "Aula desmarcada" : "Aula concluída!");
    } catch (error) {
      toast.error("Erro ao atualizar progresso");
    }
  };

  const getCurrentIndex = () => {
    return todasAulas.findIndex(a => a.id === aulaId);
  };

  const goToAula = (direction) => {
    const currentIndex = getCurrentIndex();
    const newIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;
    
    if (newIndex >= 0 && newIndex < todasAulas.length) {
      navigate(`/crm/academy/aula/${todasAulas[newIndex].id}`);
    }
  };

  const renderConteudo = () => {
    if (!aula) return null;

    switch (aula.tipo) {
      case 'video':
        return (
          <div className="aspect-video bg-black rounded-xl overflow-hidden">
            {aula.conteudo_url ? (
              // Verificar se é YouTube ou Vimeo
              aula.conteudo_url.includes('youtube') || aula.conteudo_url.includes('youtu.be') ? (
                <iframe
                  src={aula.conteudo_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                  className="w-full h-full"
                  allowFullScreen
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                />
              ) : aula.conteudo_url.includes('vimeo') ? (
                <iframe
                  src={aula.conteudo_url.replace('vimeo.com/', 'player.vimeo.com/video/')}
                  className="w-full h-full"
                  allowFullScreen
                />
              ) : (
                <video 
                  src={aula.conteudo_url} 
                  controls 
                  className="w-full h-full"
                />
              )
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white">
                <Play className="w-16 h-16 opacity-50" />
              </div>
            )}
          </div>
        );
      
      case 'pdf':
        return (
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
            {aula.conteudo_url ? (
              <div className="space-y-4">
                <iframe 
                  src={aula.conteudo_url}
                  className="w-full h-[600px] rounded-lg border border-slate-200 dark:border-slate-700"
                />
                <a 
                  href={aula.conteudo_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-primary hover:underline"
                >
                  <ExternalLink className="w-4 h-4" />
                  Abrir PDF em nova aba
                </a>
              </div>
            ) : (
              <div className="text-center py-12 text-slate-500">
                <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>PDF não disponível</p>
              </div>
            )}
          </div>
        );
      
      case 'texto':
      default:
        return (
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700">
            {aula.conteudo_texto ? (
              <SafeHtml
                className="prose dark:prose-invert max-w-none"
                html={aula.conteudo_texto}
              />
            ) : (
              <div className="text-center py-12 text-slate-500">
                <FileText className="w-16 h-16 mx-auto mb-4 opacity-50" />
                <p>Conteúdo não disponível</p>
              </div>
            )}
          </div>
        );
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-slate-900">
        <div className="flex-1 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  if (!aula) return null;

  const currentIndex = getCurrentIndex();
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < todasAulas.length - 1;
  const isLastAula = currentIndex === todasAulas.length - 1;
  const temQuestionarios = questionarios.length > 0;

  return (
    <div className="flex min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 dark:from-slate-950 dark:to-slate-900">
      
      <div className="flex-1 p-4 lg:p-8 overflow-auto pt-20 lg:pt-8">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-white dark:hover:bg-slate-800 rounded-lg transition-colors"
            >
              <ArrowLeft className="w-5 h-5 text-slate-600 dark:text-slate-400" />
            </button>
            <div className="flex-1">
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Aula {currentIndex + 1} de {todasAulas.length}
              </p>
              <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100">
                {aula.titulo}
              </h1>
            </div>
            
            {/* Botão concluir */}
            <button
              onClick={marcarConcluida}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                concluida
                  ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
              }`}
              data-testid="marcar-concluida-btn"
            >
              <CheckCircle className={`w-5 h-5 ${concluida ? 'fill-current' : ''}`} />
              {concluida ? 'Concluída' : 'Marcar como concluída'}
            </button>
          </div>

          {/* Conteúdo */}
          {renderConteudo()}
          
          {/* Descrição */}
          {aula.descricao && (
            <div className="glass-panel dark:bg-slate-900/80 rounded-xl p-4">
              <h3 className="font-medium text-slate-800 dark:text-slate-100 mb-2">Descrição</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">
                {aula.descricao}
              </p>
            </div>
          )}

          {/* Navegação entre aulas */}
          <div className="flex items-center justify-between pt-4">
            <button
              onClick={() => goToAula('prev')}
              disabled={!hasPrev}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                hasPrev
                  ? 'bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700'
                  : 'bg-slate-100 dark:bg-slate-800/50 text-slate-400 cursor-not-allowed'
              }`}
            >
              <ChevronLeft className="w-5 h-5" />
              Anterior
            </button>
            
            {hasNext ? (
              <button
                onClick={() => goToAula('next')}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-primary hover:bg-primary/90 text-white transition-colors"
              >
                Próxima
                <ChevronRight className="w-5 h-5" />
              </button>
            ) : (
              <div className="text-sm text-slate-500 dark:text-slate-400">
                Última aula do módulo
              </div>
            )}
          </div>

          {/* Seção de Questionários - Aparece na última aula ou quando tem questionários */}
          {temQuestionarios && (
            <div className="glass-panel dark:bg-slate-900/80 rounded-xl p-6 border-2 border-purple-200 dark:border-purple-800">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <ClipboardList className="w-5 h-5 text-purple-600 dark:text-purple-400" />
                </div>
                <div>
                  <h3 className="font-semibold text-slate-800 dark:text-slate-100">
                    Avaliação do Módulo
                  </h3>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    Responda o questionário para validar seu aprendizado
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                {questionarios.map((q) => {
                  const qStatus = statusQuestionarios?.questionarios?.find(s => s.questionario_id === q.id);
                  const aprovado = qStatus?.aprovado;
                  const tentativas = qStatus?.tentativas || 0;
                  const tentativasPermitidas = qStatus?.tentativas_permitidas || q.tentativas_permitidas;
                  const podeTentar = tentativasPermitidas === 0 || tentativas < tentativasPermitidas;
                  
                  return (
                    <div 
                      key={q.id}
                      className={`p-4 rounded-lg border transition-colors ${
                        aprovado
                          ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800'
                          : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-purple-300 dark:hover:border-purple-700'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          {aprovado ? (
                            <div className="w-8 h-8 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                              <Award className="w-4 h-4 text-green-600 dark:text-green-400" />
                            </div>
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                              <ClipboardList className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                            </div>
                          )}
                          <div>
                            <h4 className="font-medium text-slate-800 dark:text-slate-100">{q.titulo}</h4>
                            <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 dark:text-slate-400">
                              <span>{q.total_questoes} questões</span>
                              <span>Nota mínima: {q.nota_minima}%</span>
                              {tentativas > 0 && (
                                <span>
                                  {tentativas}/{tentativasPermitidas === 0 ? '∞' : tentativasPermitidas} tentativas
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          {aprovado ? (
                            <span className="px-3 py-1.5 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-full text-sm font-medium flex items-center gap-1">
                              <CheckCircle className="w-4 h-4" />
                              Aprovado
                            </span>
                          ) : podeTentar ? (
                            <button
                              onClick={() => navigate(`/crm/academy/questionario/${q.id}`)}
                              className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                              data-testid={`iniciar-questionario-${q.id}`}
                            >
                              {tentativas > 0 ? 'Tentar Novamente' : 'Iniciar Questionário'}
                              <ChevronRight className="w-4 h-4" />
                            </button>
                          ) : (
                            <span className="px-3 py-1.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded-full text-sm font-medium flex items-center gap-1">
                              <Lock className="w-4 h-4" />
                              Sem tentativas
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Mensagem de status geral */}
              {statusQuestionarios?.todos_aprovados && (
                <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/10 rounded-lg border border-green-200 dark:border-green-800 flex items-center gap-3">
                  <Award className="w-6 h-6 text-green-600 dark:text-green-400" />
                  <div>
                    <p className="font-medium text-green-700 dark:text-green-400">
                      Parabéns! Você completou todas as avaliações deste módulo.
                    </p>
                    <p className="text-sm text-green-600 dark:text-green-500 mt-0.5">
                      Continue para o próximo módulo do curso.
                    </p>
                  </div>
                </div>
              )}

              {!statusQuestionarios?.todos_aprovados && statusQuestionarios?.tem_questionarios && (
                <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-900/10 rounded-lg border border-amber-200 dark:border-amber-800 flex items-center gap-3">
                  <AlertCircle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                  <div>
                    <p className="font-medium text-amber-700 dark:text-amber-400">
                      Complete as avaliações para concluir o módulo
                    </p>
                    <p className="text-sm text-amber-600 dark:text-amber-500 mt-0.5">
                      Passe em todos os questionários para desbloquear o próximo módulo.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AcademyAula;
